import React, { useEffect, useState } from 'react';
import { Table, Button, Space, message, Tag, Popconfirm, Switch, Modal, Form, Input, Select } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useConfig } from '../../contexts/ConfigContext';
import { useCompany } from '../../contexts/CompanyContext';
import { useUser } from '../../contexts/UserContext';
import { useBotAdmin } from '../../contexts/BotAdminContext';
import { useBotTools } from '../../hooks/useWpbot';
import useAuth from '../../hooks/useAuth';
import wpbotService from '../../services/wpbotService';
import BotSelector from './BotSelector';
import { TTool } from '../../models/wpbotModel';
import { resizeText } from '../../utils';

const HTTP_METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"];
// Misma regla del backend: ^[a-z][a-z0-9_]{0,63}$
const TOOL_NAME_REGEX = /^[a-z][a-z0-9_]{0,63}$/;

/**
 * Lista de herramientas (tools) del bot seleccionado: toggle
 * habilitar/deshabilitar (set-tool-enabled) y CRUD básico.
 * Las tools compartidas (is_shared) o de otro bot solo se pueden
 * habilitar/deshabilitar — el backend rechaza editarlas/borrarlas (403).
 */
const ViewToolsList: React.FC = () => {
  const { config } = useConfig();
  const { company } = useCompany();
  const { user } = useUser();
  const { botAdmin } = useBotAdmin();
  const { tokenData } = useAuth.useToken('authCsrfToken', config.session, config.key);
  const [form] = Form.useForm();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTool, setEditingTool] = useState<TTool | null>(null);
  const [saving, setSaving] = useState(false);

  const botId = botAdmin.selectedBotId;
  const { data, isLoading, error, mutate } = useBotTools(tokenData, { bot_id: botId, company: company.current?.uid });

  useEffect(() => {
    if (error) {
      message.error("Error al cargar las herramientas");
    }
  }, [error]);

  const actions = user.permissions.actions.join()
  const canCreate = actions.includes('wpbot.action.tool.create')
  const canUpdate = actions.includes('wpbot.action.tool.update')
  const canToggle = actions.includes('wpbot.action.tool.toggle_status')

  const openCreate = () => {
    setEditingTool(null)
    form.resetFields()
    setModalOpen(true)
  }

  const openEdit = (tool: TTool) => {
    setEditingTool(tool)
    form.setFieldsValue({
      name: tool.name,
      description: tool.description,
      endpoint_url: tool.endpoint_url,
      http_method: tool.http_method,
      is_internal: tool.is_internal,
      is_shared: tool.is_shared,
      input_schema: tool.input_schema,
      output_schema: tool.output_schema,
    })
    setModalOpen(true)
  }

  const handleSave = async () => {
    const values = await form.validateFields()
    setSaving(true)
    const payload = {
      ...values,
      bot_id: botId,
      company: company.current?.uid,
    }
    const res = editingTool
      ? await wpbotService.updateTool(tokenData, { ...payload, id: editingTool.id })
      : await wpbotService.createTool(tokenData, payload)
    setSaving(false)
    if (res?.error) {
      message.error(res.message)
      return
    }
    message.success(editingTool ? "Herramienta actualizada" : "Herramienta creada")
    setModalOpen(false)
    mutate()
  }

  const handleToggle = async (tool: TTool, isEnabled: boolean) => {
    const res = await wpbotService.setToolEnabled(tokenData, {
      id: tool.id,
      bot_id: botId,
      is_enabled: isEnabled,
      company: company.current?.uid,
    })
    if (res?.error) {
      message.error(res.message)
      return
    }
    message.success(`Herramienta ${isEnabled ? 'habilitada' : 'deshabilitada'}`)
    mutate()
  }

  const handleDelete = async (tool: TTool) => {
    const res = await wpbotService.deleteTool(tokenData, { id: tool.id, bot_id: botId, company: company.current?.uid })
    if (res?.error) {
      message.error(res.message)
      return
    }
    message.success("Herramienta eliminada")
    mutate()
  }

  const columns = [
    {
      title: 'Nombre',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Descripción',
      dataIndex: 'description',
      key: 'description',
      render: (val: string) => resizeText(val, 80)
    },
    {
      title: 'Método',
      dataIndex: 'http_method',
      key: 'http_method',
      render: (val: string) => <Tag>{val}</Tag>
    },
    {
      title: 'Tipo',
      key: 'type',
      render: (_: any, record: TTool) => (
        <Space size={4}>
          {record.is_shared && <Tag color="blue">Compartida</Tag>}
          {record.is_internal && <Tag color="purple">Interna</Tag>}
          {!record.is_shared && record.owner_bot_id === botId && <Tag>Propia</Tag>}
        </Space>
      )
    },
    {
      title: 'Habilitada',
      key: 'is_enabled',
      render: (_: any, record: TTool) => (
        <Switch
          checked={record.is_enabled}
          disabled={!canToggle}
          onChange={(checked) => handleToggle(record, checked)}
        />
      )
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_: any, record: TTool) => {
        const isOwn = record.owner_bot_id === botId
        return (
          <Space size="middle">
            {canUpdate && isOwn && (
              <Button type="primary" ghost icon={<EditOutlined />} onClick={() => openEdit(record)}>
                Editar
              </Button>
            )}
            {canUpdate && isOwn && (
              <Popconfirm
                title="¿Eliminar esta herramienta?"
                okText="Eliminar"
                cancelText="Cancelar"
                onConfirm={() => handleDelete(record)}
              >
                <Button danger icon={<DeleteOutlined />} />
              </Popconfirm>
            )}
          </Space>
        )
      },
    },
  ];

  const tools: TTool[] = data?.status === 200 ? (data?.tools || []) : []

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: 12 }}>
        <h2>Herramientas del bot</h2>
        <Space>
          <BotSelector />
          {canCreate && (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} disabled={!botId}>
              Nueva Herramienta
            </Button>
          )}
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={tools}
        rowKey="id"
        loading={isLoading}
        locale={{ emptyText: botId ? "Este bot no tiene herramientas" : "Selecciona un bot" }}
      />

      <Modal
        title={editingTool ? "Editar herramienta" : "Nueva herramienta"}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        confirmLoading={saving}
        okText="Guardar"
        cancelText="Cancelar"
        width={640}
        maskClosable={false}
      >
        <Form form={form} layout="vertical" initialValues={{ http_method: "POST", is_internal: false, is_shared: false, input_schema: "{}", output_schema: "{}" }}>
          <Form.Item
            label="Nombre (snake_case, único)"
            name="name"
            rules={[
              { required: true, message: 'El nombre es obligatorio' },
              { pattern: TOOL_NAME_REGEX, message: 'Formato: ^[a-z][a-z0-9_]{0,63}$' },
            ]}
          >
            <Input placeholder="Ej: get_available_slots" maxLength={64} />
          </Form.Item>
          <Form.Item
            label="Descripción"
            name="description"
            rules={[{ required: true, message: 'La descripción es obligatoria' }]}
          >
            <Input.TextArea rows={2} placeholder="Qué hace la herramienta (la lee el agente IA)" />
          </Form.Item>
          <Form.Item
            label="Endpoint URL"
            name="endpoint_url"
            rules={[{ required: true, message: 'El endpoint es obligatorio' }]}
            extra="Si es interna, usa un path relativo (empieza con '/'); si no, una URL http(s)."
          >
            <Input placeholder="Ej: /api/internal/get-slots o https://..." />
          </Form.Item>
          <Space size="large" wrap>
            <Form.Item label="Método HTTP" name="http_method">
              <Select style={{ width: 120 }} options={HTTP_METHODS.map((m) => ({ value: m, label: m }))} />
            </Form.Item>
            <Form.Item label="Interna" name="is_internal" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item label="Compartida" name="is_shared" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Space>
          <Form.Item label="Schema de entrada (JSON)" name="input_schema">
            <Input.TextArea rows={3} placeholder='{"type":"object","properties":{...}}' />
          </Form.Item>
          <Form.Item label="Schema de salida (JSON)" name="output_schema">
            <Input.TextArea rows={3} placeholder='{"type":"object","properties":{...}}' />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ViewToolsList;
